/* src/sections/auth/festival/themes/UttarayanTheme.js */
import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------
// Vibrant Uttarayan Color Palette - Sky & Kites
const COLORS = {
    // Sky Gradients
    SKY_MORNING: '#87CEEB',         // Sky blue
    SKY_BRIGHT: '#4A90E2',          // Bright blue
    SKY_DEEP: '#2E5C8A',            // Deep blue
    SKY_HORIZON: '#FDB462',         // Warm horizon

    // Kite Colors (Vibrant & Diverse)
    KITE_RED: '#FF3B30',            // Bright red
    KITE_YELLOW: '#FFD700',         // Golden yellow
    KITE_GREEN: '#4CD964',          // Fresh green
    KITE_PINK: '#FF2D55',           // Hot pink
    KITE_ORANGE: '#FF9500',         // Orange
    KITE_PURPLE: '#9B59B6',         // Purple
    KITE_CYAN: '#5AC8FA',           // Cyan
    KITE_LIME: '#CDDC39',           // Lime

    // Accent Colors
    SUN_GOLD: '#FFC000',            // Sun gold
    CLOUD_WHITE: '#FFFFFF',         // Cloud white
    STRING_DARK: '#2C3E50',         // String color
    FESTIVE_SAFFRON: '#FF9933',     // Saffron

    // Special Effects
    SPARKLE_GOLD: '#FFE66D',        // Sparkle
    CELEBRATION_RED: '#E74C3C',     // Celebration
};

// Uttarayan Canvas Animation Engine
function UttarayanCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const ctx = canvas.getContext('2d');
        let animationFrameId;
        const kites = [];
        const clouds = [];
        const strings = [];
        const confetti = [];
        const birds = [];
        let textParticles = [];
        let time = 0;
        let sunPosition = { x: 0, y: 0, radius: 60 };

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            sunPosition = {
                x: canvas.width * 0.85,
                y: canvas.height * 0.15,
                radius: canvas.width < 768 ? 40 : 60
            };
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // --- 1. Kite Factory (Main Element) ---
        const kiteShapes = ['diamond', 'triangle', 'hexagon', 'butterfly'];
        const kiteColors = [
            COLORS.KITE_RED, COLORS.KITE_YELLOW, COLORS.KITE_GREEN,
            COLORS.KITE_PINK, COLORS.KITE_ORANGE, COLORS.KITE_PURPLE,
            COLORS.KITE_CYAN, COLORS.KITE_LIME
        ];

        const createKite = () => {
            const shape = kiteShapes[Math.floor(Math.random() * kiteShapes.length)];
            const color = kiteColors[Math.floor(Math.random() * kiteColors.length)];

            return {
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height * 0.7 + canvas.height * 0.2,
                size: Math.random() * 35 + 25,
                color,
                secondaryColor: kiteColors[Math.floor(Math.random() * kiteColors.length)],
                shape,
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: (Math.random() - 0.5) * 0.02,
                swayOffset: Math.random() * Math.PI * 2,
                swaySpeed: Math.random() * 0.02 + 0.01,
                swayAmplitude: Math.random() * 30 + 20,
                bobSpeed: Math.random() * 0.015 + 0.01,
                bobAmplitude: Math.random() * 15 + 10,
                stringLength: Math.random() * 100 + 80,
                tailSegments: Math.floor(Math.random() * 3) + 3,
                alpha: Math.random() * 0.3 + 0.7,
            };
        };

        const updateKite = (kite) => {
            // eslint-disable-next-line no-param-reassign
            kite.swayOffset += kite.swaySpeed;
            // eslint-disable-next-line no-param-reassign
            kite.rotation += kite.rotationSpeed;

            // Sway motion (left-right)
            // eslint-disable-next-line no-param-reassign
            kite.x += Math.sin(kite.swayOffset) * 0.5;

            // Bob motion (up-down)
            // eslint-disable-next-line no-param-reassign
            kite.y += Math.sin(time * kite.bobSpeed) * 0.3;

            // Keep kites in bounds
            if (kite.x < -50) {
                // eslint-disable-next-line no-param-reassign
                kite.x = canvas.width + 50;
            }
            if (kite.x > canvas.width + 50) {
                // eslint-disable-next-line no-param-reassign
                kite.x = -50;
            }
        };

        const drawKite = (kite) => {
            ctx.save();
            ctx.globalAlpha = kite.alpha;
            ctx.translate(kite.x, kite.y);
            ctx.rotate(kite.rotation);

            // Shadow for depth
            ctx.shadowBlur = 15;
            ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
            ctx.shadowOffsetX = 5;
            ctx.shadowOffsetY = 5;

            if (kite.shape === 'diamond') {
                // Classic diamond kite
                ctx.beginPath();
                ctx.moveTo(0, -kite.size);
                ctx.lineTo(kite.size * 0.7, 0);
                ctx.lineTo(0, kite.size);
                ctx.lineTo(-kite.size * 0.7, 0);
                ctx.closePath();

                const gradient = ctx.createLinearGradient(-kite.size, -kite.size, kite.size, kite.size);
                gradient.addColorStop(0, kite.color);
                gradient.addColorStop(0.5, kite.secondaryColor);
                gradient.addColorStop(1, kite.color);
                ctx.fillStyle = gradient;
                ctx.fill();

                // Cross pattern
                ctx.strokeStyle = COLORS.CLOUD_WHITE;
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(0, -kite.size);
                ctx.lineTo(0, kite.size);
                ctx.moveTo(-kite.size * 0.7, 0);
                ctx.lineTo(kite.size * 0.7, 0);
                ctx.stroke();

            } else if (kite.shape === 'triangle') {
                // Delta kite
                ctx.beginPath();
                ctx.moveTo(0, -kite.size * 0.8);
                ctx.lineTo(kite.size * 0.9, kite.size * 0.6);
                ctx.lineTo(-kite.size * 0.9, kite.size * 0.6);
                ctx.closePath();

                ctx.fillStyle = kite.color;
                ctx.fill();

                // Design
                ctx.fillStyle = kite.secondaryColor;
                ctx.beginPath();
                ctx.moveTo(0, -kite.size * 0.8);
                ctx.lineTo(kite.size * 0.45, kite.size * 0.6);
                ctx.lineTo(-kite.size * 0.45, kite.size * 0.6);
                ctx.closePath();
                ctx.fill();

            } else if (kite.shape === 'hexagon') {
                // Hexagonal kite
                const sides = 6;
                ctx.beginPath();
                for (let i = 0; i < sides; i += 1) {
                    const angle = (Math.PI * 2 * i) / sides - Math.PI / 2;
                    const x = Math.cos(angle) * kite.size * 0.7;
                    const y = Math.sin(angle) * kite.size * 0.7;
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.closePath();
                ctx.fillStyle = kite.color;
                ctx.fill();

                // Inner hexagon
                ctx.beginPath();
                for (let i = 0; i < sides; i += 1) {
                    const angle = (Math.PI * 2 * i) / sides - Math.PI / 2;
                    const x = Math.cos(angle) * kite.size * 0.4;
                    const y = Math.sin(angle) * kite.size * 0.4;
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.closePath();
                ctx.fillStyle = kite.secondaryColor;
                ctx.fill();

            } else {
                // Butterfly kite
                ctx.fillStyle = kite.color;
                ctx.beginPath();
                ctx.ellipse(-kite.size * 0.3, -kite.size * 0.3, kite.size * 0.5, kite.size * 0.8, -0.3, 0, Math.PI * 2);
                ctx.fill();
                ctx.beginPath();
                ctx.ellipse(kite.size * 0.3, -kite.size * 0.3, kite.size * 0.5, kite.size * 0.8, 0.3, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = kite.secondaryColor;
                ctx.beginPath();
                ctx.ellipse(-kite.size * 0.3, kite.size * 0.3, kite.size * 0.4, kite.size * 0.6, -0.2, 0, Math.PI * 2);
                ctx.fill();
                ctx.beginPath();
                ctx.ellipse(kite.size * 0.3, kite.size * 0.3, kite.size * 0.4, kite.size * 0.6, 0.2, 0, Math.PI * 2);
                ctx.fill();
            }

            // Kite tail
            ctx.strokeStyle = kite.color;
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);

            for (let i = 0; i < kite.tailSegments; i += 1) {
                const tailY = kite.size + i * 15;
                const tailX = Math.sin(time * 0.05 + i) * 10;

                if (i === 0) {
                    ctx.beginPath();
                    ctx.moveTo(0, kite.size);
                    ctx.lineTo(tailX, tailY);
                } else {
                    // const prevTailY = kite.size + (i - 1) * 15;
                    // const prevTailX = Math.sin(time * 0.05 + i - 1) * 10;
                    ctx.lineTo(tailX, tailY);
                }

                // Tail bow
                ctx.fillStyle = kite.secondaryColor;
                ctx.beginPath();
                ctx.arc(tailX, tailY, 4, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.stroke();
            ctx.setLineDash([]);

            ctx.restore();
        };

        // --- 2. Cloud Factory ---
        const createCloud = () => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height * 0.4,
            size: Math.random() * 40 + 30,
            speed: Math.random() * 0.3 + 0.1,
            alpha: Math.random() * 0.4 + 0.3,
            puffCount: Math.floor(Math.random() * 2) + 3,
        });

        const updateCloud = (cloud) => {
            // eslint-disable-next-line no-param-reassign
            cloud.x += cloud.speed;
            if (cloud.x > canvas.width + 100) {
                // eslint-disable-next-line no-param-reassign
                cloud.x = -100;
                // eslint-disable-next-line no-param-reassign
                cloud.y = Math.random() * canvas.height * 0.4;
            }
        };

        const drawCloud = (cloud) => {
            ctx.save();
            ctx.globalAlpha = cloud.alpha;
            ctx.fillStyle = COLORS.CLOUD_WHITE;
            ctx.shadowBlur = 10;
            ctx.shadowColor = 'rgba(255, 255, 255, 0.5)';

            for (let i = 0; i < cloud.puffCount; i += 1) {
                const offsetX = (i - cloud.puffCount / 2) * cloud.size * 0.5;
                const puffSize = cloud.size * (i === Math.floor(cloud.puffCount / 2) ? 1 : 0.7);

                ctx.beginPath();
                ctx.arc(cloud.x + offsetX, cloud.y, puffSize, 0, Math.PI * 2);
                ctx.fill();
            }

            ctx.restore();
        };

        // --- 3. Flying Birds ---
        const createBird = () => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height * 0.5,
            speed: Math.random() * 1 + 0.5,
            wingPhase: Math.random() * Math.PI * 2,
            wingSpeed: Math.random() * 0.1 + 0.05,
            size: Math.random() * 10 + 8,
        });

        const updateBird = (bird) => {
            // eslint-disable-next-line no-param-reassign
            bird.x += bird.speed;
            // eslint-disable-next-line no-param-reassign
            bird.wingPhase += bird.wingSpeed;

            if (bird.x > canvas.width + 50) {
                // eslint-disable-next-line no-param-reassign
                bird.x = -50;
                // eslint-disable-next-line no-param-reassign
                bird.y = Math.random() * canvas.height * 0.5;
            }
        };

        const drawBird = (bird) => {
            ctx.save();
            ctx.translate(bird.x, bird.y);

            const wingAngle = Math.sin(bird.wingPhase) * 0.5;

            ctx.strokeStyle = COLORS.STRING_DARK;
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';

            // Left wing
            ctx.beginPath();
            ctx.moveTo(-bird.size * 0.3, 0);
            ctx.quadraticCurveTo(-bird.size, -bird.size * 0.5 + wingAngle * bird.size, -bird.size * 1.5, 0);
            ctx.stroke();

            // Right wing
            ctx.beginPath();
            ctx.moveTo(bird.size * 0.3, 0);
            ctx.quadraticCurveTo(bird.size, -bird.size * 0.5 + wingAngle * bird.size, bird.size * 1.5, 0);
            ctx.stroke();

            ctx.restore();
        };

        // --- 4. Festive Confetti ---
        const createConfetti = () => ({
            x: Math.random() * canvas.width,
            y: -20,
            size: Math.random() * 6 + 3,
            color: kiteColors[Math.floor(Math.random() * kiteColors.length)],
            speedY: Math.random() * 2 + 1,
            speedX: (Math.random() - 0.5) * 1,
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.2,
            alpha: Math.random() * 0.8 + 0.2,
        });

        const updateConfetti = (conf) => {
            // eslint-disable-next-line no-param-reassign
            conf.y += conf.speedY;
            // eslint-disable-next-line no-param-reassign
            conf.x += conf.speedX;
            // eslint-disable-next-line no-param-reassign
            conf.rotation += conf.rotationSpeed;

            if (conf.y > canvas.height + 20) {
                Object.assign(conf, createConfetti());
            }
        };

        const drawConfetti = (conf) => {
            ctx.save();
            ctx.globalAlpha = conf.alpha;
            ctx.translate(conf.x, conf.y);
            ctx.rotate(conf.rotation);

            ctx.fillStyle = conf.color;
            ctx.fillRect(-conf.size / 2, -conf.size / 2, conf.size, conf.size);

            ctx.restore();
        };

        // --- 5. Animated Text ---
        const createTextParticle = (x, y, char, index) => ({
            x,
            y: y - 120,
            targetY: y,
            char,
            delay: index * 12, // Stagger delay
            time: 0,
            alpha: 0,
            scale: 0,
            bounce: 0,
        });

        const updateTextParticle = (textChar) => {
            // eslint-disable-next-line no-param-reassign
            textChar.time += 1;
            if (textChar.time > textChar.delay) {
                const progress = Math.min(1, (textChar.time - textChar.delay) / 25);
                const easeOut = 1 - (1 - progress) ** 3;

                // eslint-disable-next-line no-param-reassign
                textChar.y = textChar.targetY - (1 - easeOut) * 120;
                // eslint-disable-next-line no-param-reassign
                textChar.alpha = Math.min(1, progress * 2);
                // eslint-disable-next-line no-param-reassign
                textChar.scale = Math.min(1, progress * 1.2);

                if (progress >= 1) {
                    // eslint-disable-next-line no-param-reassign
                    textChar.bounce = Math.sin(time * 0.05) * 3;
                }
            }
        };

        const drawTextParticle = (textChar) => {
            if (textChar.time < textChar.delay) return;

            ctx.save();
            ctx.globalAlpha = textChar.alpha;
            ctx.translate(textChar.x, textChar.y + textChar.bounce);
            ctx.scale(textChar.scale, textChar.scale);

            const fontSize = canvas.width < 768 ? 38 : 58;
            ctx.font = `bold ${fontSize}px "Comic Sans MS", cursive, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Colorful gradient text
            const gradient = ctx.createLinearGradient(0, -fontSize / 2, 0, fontSize / 2);
            gradient.addColorStop(0, COLORS.KITE_RED);
            gradient.addColorStop(0.33, COLORS.KITE_YELLOW);
            gradient.addColorStop(0.66, COLORS.KITE_GREEN);
            gradient.addColorStop(1, COLORS.KITE_CYAN);

            // Glow effect
            ctx.shadowBlur = 20;
            ctx.shadowColor = COLORS.SUN_GOLD;

            ctx.fillStyle = gradient;
            ctx.fillText(textChar.char, 0, 0);

            // Outline
            ctx.strokeStyle = COLORS.CLOUD_WHITE;
            ctx.lineWidth = 3;
            ctx.strokeText(textChar.char, 0, 0);

            ctx.restore();
        };

        // --- 6. Sun Animation ---
        const drawSun = () => {
            ctx.save();

            // Sun rays
            for (let i = 0; i < 12; i += 1) {
                const angle = (Math.PI * 2 * i) / 12 + time * 0.01;
                const rayLength = sunPosition.radius * 0.4;
                const rayStart = sunPosition.radius + 5;

                const gradient = ctx.createLinearGradient(
                    sunPosition.x + Math.cos(angle) * rayStart,
                    sunPosition.y + Math.sin(angle) * rayStart,
                    sunPosition.x + Math.cos(angle) * (rayStart + rayLength),
                    sunPosition.y + Math.sin(angle) * (rayStart + rayLength)
                );
                gradient.addColorStop(0, COLORS.SUN_GOLD);
                gradient.addColorStop(1, alpha(COLORS.SUN_GOLD, 0));

                ctx.strokeStyle = gradient;
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.moveTo(
                    sunPosition.x + Math.cos(angle) * rayStart,
                    sunPosition.y + Math.sin(angle) * rayStart
                );
                ctx.lineTo(
                    sunPosition.x + Math.cos(angle) * (rayStart + rayLength),
                    sunPosition.y + Math.sin(angle) * (rayStart + rayLength)
                );
                ctx.stroke();
            }

            // Sun circle
            const sunGradient = ctx.createRadialGradient(
                sunPosition.x, sunPosition.y, 0,
                sunPosition.x, sunPosition.y, sunPosition.radius
            );
            sunGradient.addColorStop(0, COLORS.SPARKLE_GOLD);
            sunGradient.addColorStop(0.7, COLORS.SUN_GOLD);
            sunGradient.addColorStop(1, COLORS.SKY_HORIZON);

            ctx.fillStyle = sunGradient;
            ctx.shadowBlur = 30;
            ctx.shadowColor = COLORS.SUN_GOLD;
            ctx.beginPath();
            ctx.arc(sunPosition.x, sunPosition.y, sunPosition.radius, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        };

        // --- Initialize Elements ---

        // Create kites
        for (let i = 0; i < 15; i += 1) {
            kites.push(createKite());
        }

        // Create clouds
        for (let i = 0; i < 8; i += 1) {
            clouds.push(createCloud());
        }

        // Create birds
        for (let i = 0; i < 6; i += 1) {
            birds.push(createBird());
        }

        // Create confetti
        for (let i = 0; i < 50; i += 1) {
            confetti.push(createConfetti());
        }

        // Create text
        const initText = () => {
            const text = 'KAI PO CHE!';
            const isDesktop = canvas.width >= 900;
            const centerX = isDesktop ? canvas.width * 0.70 : canvas.width / 2;
            const centerY = canvas.height * 0.10;
            const charSpacing = canvas.width < 768 ? 28 : 42;
            const startX = centerX - (text.length * charSpacing) / 2;

            textParticles = text.split('').map((char, i) =>
                createTextParticle(startX + i * charSpacing, centerY, char, i)
            );
        };

        setTimeout(initText, 50); // Initial delay
        // --- Animation Loop ---
        const animate = () => {
            time += 1;

            // Clear with sky gradient
            const skyGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
            skyGradient.addColorStop(0, COLORS.SKY_BRIGHT);
            skyGradient.addColorStop(0.5, COLORS.SKY_MORNING);
            skyGradient.addColorStop(1, COLORS.SKY_HORIZON);
            ctx.fillStyle = skyGradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Draw sun
            drawSun();

            // Draw clouds
            clouds.forEach(cloud => {
                updateCloud(cloud);
                drawCloud(cloud);
            });

            // Draw birds
            birds.forEach(bird => {
                updateBird(bird);
                drawBird(bird);
            });

            // Draw kites
            kites.forEach(kite => {
                updateKite(kite);
                drawKite(kite);
            });

            // Draw confetti
            confetti.forEach(conf => {
                updateConfetti(conf);
                drawConfetti(conf);
            });

            // Draw text
            textParticles.forEach(textChar => {
                updateTextParticle(textChar);
                drawTextParticle(textChar);
            });

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        // Handle resize
        const handleResize = () => {
            resizeCanvas();
            if (textParticles.length > 0) initText();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 2,
            }}
        />
    );
}

// Main Uttarayan Theme Component
export default function UttarayanTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            width: '100vw', height: '100vh', position: 'relative',
            display: 'flex', alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 12 },
            overflow: 'hidden',
        }}>
            <UttarayanCanvas />
            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 3, position: 'relative',
                bgcolor: isDark ? 'rgba(0,8,20,0.97)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha(COLORS.KITE_YELLOW, 0.4)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.4)}`,
                    `0 16px 64px ${alpha(COLORS.KITE_CYAN, 0.2)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px',
                    background: `linear-gradient(90deg, transparent, ${COLORS.KITE_RED}, ${COLORS.KITE_YELLOW}, ${COLORS.KITE_GREEN}, ${COLORS.KITE_CYAN}, transparent)`,
                    borderRadius: '0 0 4px 4px',
                    animation: 'uttarayanGlow 3s ease-in-out infinite',
                },
                '@keyframes uttarayanGlow': { '0%, 100%': { opacity: 0.7 }, '50%': { opacity: 1 } },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: `linear-gradient(90deg, ${COLORS.KITE_RED}, ${COLORS.KITE_YELLOW}, ${COLORS.KITE_GREEN}, ${COLORS.KITE_CYAN}, ${COLORS.KITE_RED})`,
                        backgroundSize: '400% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#fff', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha(COLORS.KITE_CYAN, 0.5)}`,
                    }}>🨁 &nbsp; Happy Uttarayan &nbsp; 🨁</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2, position: 'relative', zIndex: 2 }}><Logo /></Stack>
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    {cloneElement(children, { hideHelpLink: true })}
                </Box>
            </Card>
            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha(COLORS.SKY_BRIGHT, 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#fff', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

UttarayanTheme.propTypes = {
    children: PropTypes.node,
};
